import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Download, IndianRupee, GraduationCap, Upload, Save, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loading } from "@/components/shared";
import api from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><Icon className="h-[18px] w-[18px]" strokeWidth={1.75} /></div>
      <p className="mt-3 font-heading text-2xl font-bold text-navy">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
      {sub && <p className="mt-0.5 text-xs font-semibold text-emerald">{sub}</p>}
    </div>
  );
}

export function FunnelAdmin() {
  const { data, isLoading } = useQuery({ queryKey: ["funnel"], queryFn: async () => (await api.get("/admin/funnel")).data });
  if (isLoading) return <Loading />;
  const s = data.subscribers, c = data.conversions;
  const rungs = [
    ["Subscribers", s.total, null],
    ["Confirmed", s.confirmed, `${c.subscribe_to_confirm}% of subscribers`],
    ["Magnet downloads", data.magnet_downloads, `${c.confirm_to_magnet}% of confirmed`],
    ["Playbook sales", data.tripwire.sales, `${c.confirm_to_tripwire}% of confirmed`],
    ["Course sales", data.course.sales, `${c.tripwire_to_course}% of playbook buyers`],
  ];
  return (
    <div className="space-y-6" data-testid="funnel-admin">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Subscribers today" value={s.today} sub={`${s.week} this week`} />
        <Stat icon={Users} label="Total subscribers" value={s.total} sub={`${s.confirmed} confirmed`} />
        <Stat icon={Download} label="Magnet downloads" value={data.magnet_downloads} />
        <Stat icon={IndianRupee} label="Total revenue" value={inr(data.tripwire.revenue + data.course.revenue)} sub={`${data.tripwire.sales} playbooks · ${data.course.sales} course`} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-navy"><TrendingUp className="h-4 w-4 text-emerald" /> The funnel</h3>
        <div className="mt-4 space-y-2">
          {rungs.map(([label, value, sub], i) => (
            <div key={label} className="flex items-center gap-3" data-testid={`funnel-rung-${i}`}>
              <div className="h-9 rounded-md bg-emerald/10" style={{ width: `${Math.max(6, (value / Math.max(1, rungs[0][1])) * 100)}%` }} />
              <span className="text-sm font-semibold text-navy">{label}: {value}</span>
              {sub && <span className="text-xs text-slate-400">{sub}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading font-semibold text-navy">Subscribers by source</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(s.by_source).map(([k, v]) => (
            <span key={k} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-navy" data-testid={`funnel-source-${k}`}>{k}: {v}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function FileButton({ onUploaded, label = "Upload" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" hidden onChange={(e) => up(e.target.files[0])} />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()}><Upload className="mr-1.5 h-3.5 w-3.5" /> {busy ? "…" : label}</Button>
    </>
  );
}

export function PlaybooksAdmin() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["pb-admin"], queryFn: async () => (await api.get("/playbooks/admin/all")).data });
  if (isLoading) return <Loading />;
  const save = async (p, patch) => {
    try {
      await api.put(`/playbooks/admin/products/${p.id}`, {
        slug: p.slug, title: p.title, module: p.module, description: p.description,
        price: p.price, cover_image: p.cover_image || "", file_url: p.file_url || "",
        file_name: p.file_name || "", status: p.status, available_date: p.available_date || "",
        is_bundle: p.is_bundle, ...patch,
      });
      toast.success("Saved"); refetch();
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="space-y-3" data-testid="playbooks-admin">
      {(data || []).map((p) => (
        <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`pb-row-${p.slug}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex-1 text-sm font-semibold text-navy">{p.is_bundle ? "★ " : `M${p.module} · `}{p.title} <span className="text-slate-400">{inr(p.price)}</span></span>
            <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${p.status === "available" ? "bg-emerald/10 text-emerald" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
            {p.file_url ? <a href={p.file_url} target="_blank" rel="noreferrer" className="text-xs text-emerald hover:underline">file ✓</a> : <span className="text-xs text-amber-600">no file</span>}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input defaultValue={p.available_date} placeholder="Available date" className="h-8 w-40 text-xs" onBlur={(e) => { p.available_date = e.target.value; }} data-testid={`pb-date-${p.slug}`} />
            <Input type="number" defaultValue={p.price} className="h-8 w-24 text-xs" onBlur={(e) => { p.price = Number(e.target.value); }} />
            <FileButton onUploaded={(f) => save(p, { file_url: f.url, file_name: f.filename, status: "available" })} />
            <Button size="sm" variant="outline" onClick={() => save(p, { status: p.status === "available" ? "coming_soon" : "available" })} data-testid={`pb-toggle-${p.slug}`}>{p.status === "available" ? "Set coming soon" : "Set available"}</Button>
            <Button size="sm" onClick={() => save(p, {})} className="bg-emerald text-white hover:bg-emerald-700" data-testid={`pb-save-${p.slug}`}><Save className="mr-1.5 h-3.5 w-3.5" /> Save</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function LeadMagnetAdmin() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["lm-admin"], queryFn: async () => (await api.get("/admin/lead-magnets")).data });
  if (isLoading) return <Loading />;
  const save = async (m, patch) => {
    try {
      await api.put(`/admin/lead-magnets/${m.id}`, {
        slug: m.slug, name: m.name, description: m.description || "",
        file_url: m.file_url || "", file_name: m.file_name || "", active: m.active, ...patch,
      });
      toast.success("Saved"); refetch();
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="space-y-3" data-testid="lead-magnet-admin">
      {(data || []).map((m) => (
        <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`lm-row-${m.slug}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-heading font-semibold text-navy">{m.name} {m.active && <span className="ml-2 rounded bg-emerald/10 px-2 py-0.5 text-[11px] font-bold text-emerald">Active</span>}</p>
              <p className="text-xs text-slate-500">{m.download_count} downloads · {m.file_url ? <a href={m.file_url} target="_blank" rel="noreferrer" className="text-emerald hover:underline">file attached</a> : <span className="text-amber-600">no file yet</span>}</p>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <Input defaultValue={m.name} className="h-9 text-sm" onBlur={(e) => { m.name = e.target.value; }} data-testid={`lm-name-${m.slug}`} />
            <Textarea defaultValue={m.description} className="text-sm" rows={2} onBlur={(e) => { m.description = e.target.value; }} />
            <div className="flex flex-wrap items-center gap-2">
              <FileButton onUploaded={(f) => save(m, { file_url: f.url, file_name: f.filename })} label="Upload / replace file" />
              {!m.active && <Button size="sm" variant="outline" onClick={() => save(m, { active: true })} data-testid={`lm-activate-${m.slug}`}>Make active</Button>}
              <Button size="sm" onClick={() => save(m, {})} className="bg-emerald text-white hover:bg-emerald-700" data-testid={`lm-save-${m.slug}`}><Save className="mr-1.5 h-3.5 w-3.5" /> Save</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function SequenceAdmin() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["seq-admin"], queryFn: async () => (await api.get("/admin/sequence")).data });
  if (isLoading) return <Loading />;
  const save = async (s) => {
    try {
      await api.put(`/admin/sequence/${s.id}`, {
        subject: s.subject, body: s.body, cta_link: s.cta_link || "", cta_text: s.cta_text || "",
        delay_days: Number(s.delay_days), is_sales_step: s.is_sales_step,
        include_magnet: s.include_magnet, active: s.active,
      });
      toast.success(`Step ${s.step_number} saved`); refetch();
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="space-y-4" data-testid="sequence-admin">
      <p className="text-sm text-slate-500">Welcome sequence. Triggered on double-opt-in confirmation, runs alongside the daily. Sales steps are skipped for anyone who has bought.</p>
      {(data || []).map((s) => (
        <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`seq-step-${s.step_number}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-heading font-semibold text-navy">Step {s.step_number}</span>
            <span className="text-xs text-slate-400">Day <Input type="number" defaultValue={s.delay_days} className="inline-block h-7 w-16 text-xs" onBlur={(e) => { s.delay_days = e.target.value; }} /></span>
            <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" defaultChecked={s.is_sales_step} onChange={(e) => { s.is_sales_step = e.target.checked; }} data-testid={`seq-sales-${s.step_number}`} /> Sales step</label>
            <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" defaultChecked={s.include_magnet} onChange={(e) => { s.include_magnet = e.target.checked; }} /> Include magnet</label>
            <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" defaultChecked={s.active} onChange={(e) => { s.active = e.target.checked; }} /> Active</label>
            <span className="ml-auto rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500" data-testid={`seq-reached-${s.step_number}`}>{s.reached} reached</span>
          </div>
          <div className="mt-3 space-y-2">
            <div><Label className="text-xs">Subject</Label><Input defaultValue={s.subject} className="mt-1 h-9 text-sm" onBlur={(e) => { s.subject = e.target.value; }} data-testid={`seq-subject-${s.step_number}`} /></div>
            <div><Label className="text-xs">Body (markdown)</Label><Textarea defaultValue={s.body} rows={4} className="mt-1 text-sm" onBlur={(e) => { s.body = e.target.value; }} /></div>
            <div className="flex gap-2">
              <Input defaultValue={s.cta_link} placeholder="CTA link (optional)" className="h-9 text-sm" onBlur={(e) => { s.cta_link = e.target.value; }} />
              <Input defaultValue={s.cta_text} placeholder="CTA text" className="h-9 text-sm" onBlur={(e) => { s.cta_text = e.target.value; }} />
            </div>
            <Button size="sm" onClick={() => save(s)} className="bg-emerald text-white hover:bg-emerald-700" data-testid={`seq-save-${s.step_number}`}><Save className="mr-1.5 h-3.5 w-3.5" /> Save step</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
